import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { countries } from '@/data/countries';

// Import branded assets
import paynovusLogoHeader from '@/assets/paynovus-logo-horizontal.png';
import paynovusWatermark from '@/assets/paynovus-watermark-bg.png';
import paynovusLetterheadHeader from '@/assets/paynovus-letterhead-header.jpg';
import paynovusLetterheadFooter from '@/assets/paynovus-letterhead-footer.jpg';

// Demo images
const DEMO_IMAGES = {
  passport: '/demo/passport-sample.jpg',
  selfie: '/demo/selfie-sample.jpg',
  liveness: '/demo/liveness-frame.jpg',
  poa: '/demo/poa-utility-bill.jpg',
};

// Types
interface ApplicationData {
  email?: string;
  phoneNumber?: string;
  countryCode?: string;
  accountType?: string;
  firstName?: string;
  lastName?: string;
  extractedFirstName?: string;
  extractedLastName?: string;
  dateOfBirth?: string;
  extractedDateOfBirth?: string;
  extractedNationality?: string;
  nationality?: string;
  countryOfResidence?: string;
  address?: { street?: string; city?: string; postalCode?: string; country?: string };
  extractedAddress?: { street?: string; city?: string; postalCode?: string; country?: string };
  documentType?: string;
  extractedDocumentNumber?: string;
  extractedDocumentCountry?: string;
  extractedDocumentIssueDate?: string;
  extractedDocumentExpiry?: string;
  employmentStatus?: string;
  employerDetails?: { name?: string; jobTitle?: string };
  industry?: string;
  annualIncome?: string;
  sourceOfFunds?: string[];
  taxResidencies?: { name?: string; tin?: string }[];
  isUSResident?: boolean;
  isPEP?: boolean;
  pepDetails?: string;
  pepSignature?: string;
  paymentDestinations?: { name?: string }[];
  documentSignature?: string;
  documentsSignedAt?: string;
  corporateCompany?: {
    name?: string;
    authorizedPerson?: { firstName?: string; lastName?: string };
  };
  idDocumentImageUrl?: string;
  selfieImageUrl?: string;
  livenessImageUrl?: string;
  poaImageUrl?: string;
  signedDocuments?: string[];
}

// Professional fintech colors - enhanced contrast
const COLORS = {
  navy: [23, 42, 69] as [number, number, number],
  navyLight: [45, 75, 120] as [number, number, number],
  accent: [132, 204, 22] as [number, number, number],
  accentDark: [101, 163, 13] as [number, number, number],
  gray: [100, 116, 139] as [number, number, number],
  grayLight: [148, 163, 184] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  black: [0, 0, 0] as [number, number, number],
  success: [16, 185, 129] as [number, number, number],
  lightBg: [248, 250, 252] as [number, number, number],
  sectionBg: [230, 240, 250] as [number, number, number],
  border: [23, 42, 69] as [number, number, number],
  borderLight: [180, 195, 220] as [number, number, number],
  boxBg: [240, 248, 255] as [number, number, number],
};

// Page dimensions constants
const PAGE_MARGIN = 20;
const CONTENT_START_Y = 35; // Adjusted for smaller header with logo
const CONTENT_END_Y = 275; // Adjusted for smaller footer

// Get country flag
function getCountryFlag(countryName: string): string {
  const country = countries.find(c => 
    c.name.toLowerCase() === countryName?.toLowerCase() || 
    c.code.toLowerCase() === countryName?.toLowerCase()
  );
  return country?.flag || '🌍';
}

// Get full name
function getFullName(data: ApplicationData, isCorporate: boolean): string {
  if (isCorporate && data.corporateCompany?.authorizedPerson) {
    const ap = data.corporateCompany.authorizedPerson;
    return `${ap.firstName || ''} ${ap.lastName || ''}`.trim() || 'Authorized Person';
  }
  if (data.firstName && data.lastName) {
    return `${data.firstName} ${data.lastName}`;
  }
  if (data.extractedFirstName && data.extractedLastName) {
    return `${data.extractedFirstName} ${data.extractedLastName}`;
  }
  return 'Applicant';
}

// Add branded letterhead to a page
// NOTE: letterhead images are named based on content, not position
// - paynovusLetterheadFooter contains the LOGO (goes in header)
// - paynovusLetterheadHeader contains the ADDRESS INFO (goes in footer)
function addLetterhead(doc: jsPDF, pageNum: number, totalPages: number) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  doc.setPage(pageNum);
  
  // Header with LOGO (from letterhead-footer.jpg which contains the logo)
  try {
    doc.addImage(paynovusLetterheadFooter, 'JPEG', 0, 0, pageWidth, 25);
  } catch {
    // Fallback: draw header manually with logo
    doc.setFillColor(...COLORS.white);
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    try {
      doc.addImage(paynovusLogoHeader, 'PNG', 14, 6, 50, 16);
    } catch {
      doc.setTextColor(...COLORS.navy);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('PayNovus', 14, 18);
    }
  }
  
  // Page number in header (except cover)
  if (pageNum > 1) {
    doc.setTextColor(...COLORS.gray);
    doc.setFontSize(9);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, 16, { align: 'right' });
  }
  
  // Footer with ADDRESS INFO (from letterhead-header.jpg which contains address)
  try {
    doc.addImage(paynovusLetterheadHeader, 'JPEG', 0, pageHeight - 22, pageWidth, 22);
  } catch {
    // Fallback footer
    doc.setDrawColor(...COLORS.borderLight);
    doc.setLineWidth(0.5);
    doc.line(14, pageHeight - 22, pageWidth - 14, pageHeight - 22);
    
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray);
    doc.text('Pozitano 2 sq., Perform Business Center, fl. 3, 1000 Sofia, Bulgaria', 14, pageHeight - 14);
    doc.text('office@paynovus.com | paynovus.com | +35924373301', pageWidth - 14, pageHeight - 14, { align: 'right' });
  }
}

// Draw section header with prominent box
function drawSection(doc: jsPDF, title: string, num: string, yPos: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Section header bar
  doc.setFillColor(...COLORS.navy);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 12, 3, 3, 'F');
  
  // Accent stripe on left
  doc.setFillColor(...COLORS.accent);
  doc.roundedRect(PAGE_MARGIN, yPos, 4, 12, 2, 0, 'F');
  doc.rect(PAGE_MARGIN + 2, yPos, 2, 12, 'F');
  
  // Number badge
  doc.setFillColor(...COLORS.accent);
  doc.circle(PAGE_MARGIN + 12, yPos + 6, 5, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text(num, PAGE_MARGIN + 12, yPos + 7.5, { align: 'center' });
  
  // Title
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.white);
  doc.text(title.toUpperCase(), PAGE_MARGIN + 22, yPos + 7.5);
  
  return yPos + 16;
}

// Draw content box with clean border and background
function drawContentBox(doc: jsPDF, yStart: number, yEnd: number): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const boxX = PAGE_MARGIN + 2;
  const boxWidth = pageWidth - (PAGE_MARGIN * 2) - 4;
  const boxHeight = yEnd - yStart;
  
  // Light background fill
  doc.setFillColor(...COLORS.boxBg);
  doc.roundedRect(boxX, yStart, boxWidth, boxHeight, 4, 4, 'F');
  
  // Single clean navy border
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1);
  doc.roundedRect(boxX, yStart, boxWidth, boxHeight, 4, 4, 'S');
  
  // Left accent stripe (clean rectangle, not overlapping)
  doc.setFillColor(...COLORS.accent);
  doc.rect(boxX, yStart + 4, 3, boxHeight - 8, 'F');
}

// Draw a document section header for contractual docs
function drawDocumentHeader(doc: jsPDF, title: string, yPos: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header bar
  doc.setFillColor(...COLORS.navy);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 14, 3, 3, 'F');
  
  // Accent stripe
  doc.setFillColor(...COLORS.accent);
  doc.roundedRect(PAGE_MARGIN, yPos, 5, 14, 3, 0, 'F');
  doc.rect(PAGE_MARGIN + 3, yPos, 2, 14, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text(title, pageWidth / 2, yPos + 9, { align: 'center' });
  
  return yPos + 20;
}

// Add a new page and return starting Y position
function addNewPage(doc: jsPDF): number {
  doc.addPage();
  return CONTENT_START_Y;
}

// Check if we need a new page
function checkPageBreak(doc: jsPDF, yPos: number, requiredSpace: number): number {
  if (yPos + requiredSpace > CONTENT_END_Y) {
    return addNewPage(doc);
  }
  return yPos;
}

// Convert signature to black (for visibility against white/light backgrounds)
function convertSignatureToBlack(signatureDataUrl: string): string {
  // Create a canvas to process the signature
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return signatureDataUrl;
  
  const img = new Image();
  img.src = signatureDataUrl;
  
  // Since we can't do async operations, we'll return original
  // The signature should already be visible, but we can add a note
  return signatureDataUrl;
}

// Main PDF generation
export function generateComprehensiveApplicationPDF(data: ApplicationData, options?: { includeSignature?: boolean }) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  const isCorporate = data.accountType === 'corporate';
  const fullName = getFullName(data, isCorporate);
  const accountTypeText = isCorporate ? 'Corporate Account' : 'Personal Account';
  
  const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const currentTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  
  // Business rule: Individual → PN, Corporate → CPN
  const refPrefix = isCorporate ? 'CPN' : 'PN';
  const applicationRef = data.email 
    ? `${refPrefix}-${data.email.substring(0, 3).toUpperCase()}-${Date.now().toString(36).toUpperCase().slice(-6)}` 
    : `${refPrefix}-APP-REF`;
    
  const residentialAddress = data.extractedAddress || data.address || {};
  const nationalityText = data.extractedNationality || data.nationality || 'Not provided';
  const residenceCountry = data.countryOfResidence || residentialAddress.country || 'Not provided';
  
  // ========== PAGE 1: COVER ==========
  // Watermark
  try {
    doc.saveGraphicsState();
    doc.setGState(new (doc as any).GState({ opacity: 0.05 }));
    doc.addImage(paynovusWatermark, 'PNG', pageWidth / 2 - 50, pageHeight / 2 - 60, 100, 120);
    doc.restoreGraphicsState();
  } catch {}
  
  // Cover header with LOGO (from letterhead-footer.jpg which contains the logo)
  try {
    doc.addImage(paynovusLetterheadFooter, 'JPEG', 0, 0, pageWidth, 30);
  } catch {
    doc.setFillColor(...COLORS.white);
    doc.rect(0, 0, pageWidth, 35, 'F');
    try {
      doc.addImage(paynovusLogoHeader, 'PNG', pageWidth / 2 - 40, 8, 80, 22);
    } catch {
      doc.setTextColor(...COLORS.navy);
      doc.setFontSize(32);
      doc.setFont('helvetica', 'bold');
      doc.text('PayNovus', pageWidth / 2, 25, { align: 'center' });
    }
  }
  
  // Title
  doc.setTextColor(...COLORS.navy);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('APPLICATION FORM', pageWidth / 2, 65, { align: 'center' });
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(accountTypeText, pageWidth / 2, 78, { align: 'center' });
  
  // Summary card with prominent styling
  const summaryY = 95;
  doc.setFillColor(...COLORS.sectionBg);
  doc.roundedRect(25, summaryY, pageWidth - 50, 95, 6, 6, 'F');
  
  // Outer border
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(2);
  doc.roundedRect(25, summaryY, pageWidth - 50, 95, 6, 6, 'S');
  
  // Header bar
  doc.setFillColor(...COLORS.navy);
  doc.roundedRect(25, summaryY, pageWidth - 50, 18, 6, 0, 'F');
  doc.rect(25, summaryY + 14, pageWidth - 50, 4, 'F');
  
  // Accent stripe
  doc.setFillColor(...COLORS.accent);
  doc.roundedRect(25, summaryY, 6, 18, 4, 0, 'F');
  doc.rect(29, summaryY, 2, 18, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('APPLICATION SUMMARY', pageWidth / 2, summaryY + 12, { align: 'center' });
  
  let y = summaryY + 30;
  const summaryData = [
    ['Applicant:', fullName],
    ['Email:', data.email || 'Not provided'],
    ['Reference:', applicationRef],
    ['Account Type:', accountTypeText],
    ['Date:', currentDate],
  ];
  
  summaryData.forEach(([label, value]) => {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.gray);
    doc.text(label, 38, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.navy);
    doc.text(value, 85, y);
    y += 13;
  });
  
  // Cover footer with ADDRESS INFO (from letterhead-header.jpg which contains address)
  try {
    doc.addImage(paynovusLetterheadHeader, 'JPEG', 0, pageHeight - 22, pageWidth, 22);
  } catch {
    doc.setDrawColor(...COLORS.borderLight);
    doc.setLineWidth(0.5);
    doc.line(35, pageHeight - 30, pageWidth - 35, pageHeight - 30);
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.gray);
    doc.text('Pozitano 2 sq., Perform Business Center, fl. 3, 1000 Sofia, Bulgaria', pageWidth / 2, pageHeight - 22, { align: 'center' });
    doc.text('office@paynovus.com | paynovus.com | +35924373301', pageWidth / 2, pageHeight - 16, { align: 'center' });
  }
  
  doc.setTextColor(...COLORS.grayLight);
  doc.setFontSize(8);
  doc.text(`Generated: ${currentDate} at ${currentTime}`, pageWidth / 2, pageHeight - 8, { align: 'center' });

  // ========== PAGE 2: APPLICATION DETAILS ==========
  doc.addPage();
  let yPos = CONTENT_START_Y;
  
  // Page header
  yPos = drawDocumentHeader(doc, 'APPLICATION DETAILS', yPos);
  
  // Section 1: Personal Info
  yPos = drawSection(doc, 'Personal Information', '1', yPos);
  const section1Start = yPos;
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      ['Full Name', fullName],
      ['Email', data.email || 'Not provided'],
      ['Phone', data.phoneNumber ? `${data.countryCode || ''}${data.phoneNumber}` : 'Not provided'],
      ['Date of Birth', data.dateOfBirth || data.extractedDateOfBirth || 'Not provided'],
      [`${getCountryFlag(nationalityText)} Nationality`, nationalityText],
      [`${getCountryFlag(residenceCountry)} Residence`, residenceCountry],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, section1Start, yPos);
  yPos += 10;
  
  // Section 2: Address
  yPos = drawSection(doc, 'Residential Address (from POA)', '2', yPos);
  const section2Start = yPos;
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      ['Street', residentialAddress.street || 'Not provided'],
      ['City', residentialAddress.city || 'Not provided'],
      ['Postal Code', residentialAddress.postalCode || 'Not provided'],
      [`${getCountryFlag(residentialAddress.country || residenceCountry)} Country`, residentialAddress.country || residenceCountry],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, section2Start, yPos);
  yPos += 10;
  
  // Section 3: Identity Document
  yPos = drawSection(doc, 'Identity Document', '3', yPos);
  const section3Start = yPos;
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      ['Document Type', (data.documentType || 'Passport').charAt(0).toUpperCase() + (data.documentType || 'passport').slice(1)],
      ['Document Number', data.extractedDocumentNumber || 'Not provided'],
      [`${getCountryFlag(data.extractedDocumentCountry || '')} Issuing Country`, data.extractedDocumentCountry || 'Not provided'],
      ['Issue Date', data.extractedDocumentIssueDate || 'Not provided'],
      ['Expiry Date', data.extractedDocumentExpiry || 'Not provided'],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, section3Start, yPos);
  
  // ========== PAGE 3: FINANCIAL & COMPLIANCE ==========
  doc.addPage();
  yPos = CONTENT_START_Y;
  
  yPos = drawDocumentHeader(doc, 'FINANCIAL & COMPLIANCE INFORMATION', yPos);
  
  // Section 4: Employment
  yPos = drawSection(doc, 'Employment & Income', '4', yPos);
  const section4Start = yPos;
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      ['Employment Status', (data.employmentStatus || 'Not provided').charAt(0).toUpperCase() + (data.employmentStatus || '').slice(1)],
      ['Position', data.employerDetails?.jobTitle || 'Not provided'],
      ['Industry', data.industry || 'Not provided'],
      ['Employer', data.employerDetails?.name || 'Not provided'],
      ['Annual Income', data.annualIncome || 'Not provided'],
      ['Source of Funds', Array.isArray(data.sourceOfFunds) ? data.sourceOfFunds.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ') : 'Not provided'],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, section4Start, yPos);
  yPos += 10;
  
  // Section 5: Tax Info
  const taxCountry = data.taxResidencies?.[0];
  yPos = drawSection(doc, 'Tax Information', '5', yPos);
  const section5Start = yPos;
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      [`${getCountryFlag(taxCountry?.name || residenceCountry)} Tax Residency`, taxCountry?.name || residenceCountry],
      ['TIN', taxCountry?.tin || 'Not provided'],
      ['US Person (FATCA)', data.isUSResident ? 'Yes' : 'No'],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, section5Start, yPos);
  yPos += 10;
  
  // Section 6: PEP
  yPos = drawSection(doc, 'Politically Exposed Person (PEP)', '6', yPos);
  const section6Start = yPos;
  
  const pepRows: string[][] = [['PEP Status', data.isPEP ? 'Yes' : 'No']];
  if (data.isPEP && data.pepDetails) {
    pepRows.push(['Details', data.pepDetails]);
  }
  
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: pepRows,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
      1: { textColor: data.isPEP ? [239, 68, 68] : COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  
  // Add PEP signature if available
  if (data.pepSignature) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.gray);
    doc.text('PEP Declaration Signature:', PAGE_MARGIN + 8, yPos + 6);
    
    try {
      // Draw signature box with border
      doc.setFillColor(...COLORS.white);
      doc.setDrawColor(...COLORS.navy);
      doc.setLineWidth(1);
      doc.roundedRect(PAGE_MARGIN + 8, yPos + 10, 80, 30, 3, 3, 'FD');
      doc.addImage(data.pepSignature, 'PNG', PAGE_MARGIN + 10, yPos + 12, 76, 26);
      yPos += 45;
    } catch {
      yPos += 10;
    }
  } else {
    yPos += 4;
  }
  
  drawContentBox(doc, section6Start, yPos);
  yPos += 10;
  
  // Section 7: Payment Destinations
  if (data.paymentDestinations && data.paymentDestinations.length > 0) {
    yPos = drawSection(doc, 'Payment Destinations', '7', yPos);
    const section7Start = yPos;
    
    const destinations = data.paymentDestinations.map((p: any) => {
      const name = p.name || p;
      return `${getCountryFlag(name)} ${name}`;
    }).join('  •  ');
    
    autoTable(doc, {
      startY: yPos + 2,
      head: [],
      body: [['Countries', destinations]],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 55, textColor: COLORS.gray },
        1: { textColor: COLORS.navy }
      },
      margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
    });
    yPos = (doc as any).lastAutoTable.finalY + 4;
    drawContentBox(doc, section7Start, yPos);
  }

  // ========== PAGE 4: VERIFICATION DOCUMENTS ==========
  doc.addPage();
  yPos = CONTENT_START_Y;
  
  yPos = drawDocumentHeader(doc, 'VERIFICATION DOCUMENTS', yPos);
  
  const idImageUrl = data.idDocumentImageUrl || DEMO_IMAGES.passport;
  const selfieUrl = data.selfieImageUrl || DEMO_IMAGES.selfie;
  const livenessUrl = data.livenessImageUrl || DEMO_IMAGES.liveness;
  const poaUrl = data.poaImageUrl || DEMO_IMAGES.poa;
  const isDemo = !data.idDocumentImageUrl && !data.selfieImageUrl && !data.poaImageUrl;
  
  // Demo notice
  if (isDemo) {
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(245, 158, 11);
    doc.setLineWidth(0.75);
    doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 12, 3, 3, 'FD');
    doc.setFontSize(9);
    doc.setTextColor(180, 83, 9);
    doc.text('⚠ Sample documents shown for demonstration purposes', pageWidth / 2, yPos + 7.5, { align: 'center' });
    yPos += 18;
  }
  
  // ID Document Section
  yPos = drawSection(doc, 'Identity Document', 'A', yPos);
  
  // Image container with border
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(PAGE_MARGIN + 4, yPos + 2, 68, 50, 4, 4, 'FD');
  
  try {
    doc.addImage(idImageUrl, 'JPEG', PAGE_MARGIN + 6, yPos + 4, 64, 46);
  } catch {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.grayLight);
    doc.text('ID Document', PAGE_MARGIN + 38, yPos + 27, { align: 'center' });
  }
  
  // ID details box
  const detailX = PAGE_MARGIN + 80;
  doc.setFillColor(...COLORS.boxBg);
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(detailX - 4, yPos + 2, 78, 50, 4, 4, 'FD');
  
  let detailY = yPos + 14;
  [
    ['Type:', (data.documentType || 'Passport').charAt(0).toUpperCase() + (data.documentType || 'passport').slice(1)],
    ['Number:', data.extractedDocumentNumber || 'XX1234567'],
    ['Expiry:', data.extractedDocumentExpiry || '2030-01-01'],
  ].forEach(([label, value]) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.gray);
    doc.text(label, detailX, detailY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.navy);
    doc.text(value, detailX + 28, detailY);
    detailY += 11;
  });
  
  // Verified badge
  doc.setFillColor(...COLORS.success);
  doc.roundedRect(detailX, detailY + 2, 50, 10, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('✓ VERIFIED', detailX + 25, detailY + 8.5, { align: 'center' });
  
  yPos += 60;
  
  // Biometrics Section
  yPos = drawSection(doc, 'Biometric Verification', 'B', yPos);
  
  // Selfie box
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(PAGE_MARGIN + 4, yPos + 2, 48, 52, 4, 4, 'FD');
  try {
    doc.addImage(selfieUrl, 'JPEG', PAGE_MARGIN + 6, yPos + 4, 44, 48);
  } catch {
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayLight);
    doc.text('Selfie', PAGE_MARGIN + 28, yPos + 28, { align: 'center' });
  }
  
  // Liveness box
  doc.setFillColor(...COLORS.white);
  doc.roundedRect(PAGE_MARGIN + 56, yPos + 2, 48, 52, 4, 4, 'FD');
  try {
    doc.addImage(livenessUrl, 'JPEG', PAGE_MARGIN + 58, yPos + 4, 44, 48);
  } catch {
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayLight);
    doc.text('Liveness', PAGE_MARGIN + 80, yPos + 28, { align: 'center' });
  }
  
  // Status badges box
  const badgeX = PAGE_MARGIN + 112;
  doc.setFillColor(...COLORS.boxBg);
  doc.setDrawColor(...COLORS.navy);
  doc.roundedRect(badgeX - 4, yPos + 2, 62, 52, 4, 4, 'FD');
  
  let badgeY = yPos + 12;
  
  doc.setFillColor(...COLORS.success);
  doc.roundedRect(badgeX, badgeY, 54, 16, 3, 3, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('✓ LIVENESS', badgeX + 27, badgeY + 7, { align: 'center' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Anti-spoofing OK', badgeX + 27, badgeY + 13, { align: 'center' });
  
  badgeY += 22;
  doc.setFillColor(...COLORS.success);
  doc.roundedRect(badgeX, badgeY, 54, 16, 3, 3, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('✓ FACE MATCH', badgeX + 27, badgeY + 7, { align: 'center' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Matched with ID', badgeX + 27, badgeY + 13, { align: 'center' });
  
  yPos += 62;
  
  // POA Section
  yPos = drawSection(doc, 'Proof of Address', 'C', yPos);
  
  // POA image box
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(PAGE_MARGIN + 4, yPos + 2, 52, 48, 4, 4, 'FD');
  try {
    doc.addImage(poaUrl, 'JPEG', PAGE_MARGIN + 6, yPos + 4, 48, 44);
  } catch {
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayLight);
    doc.text('POA', PAGE_MARGIN + 30, yPos + 26, { align: 'center' });
  }
  
  // Address details box
  const addrX = PAGE_MARGIN + 64;
  doc.setFillColor(...COLORS.boxBg);
  doc.setDrawColor(...COLORS.navy);
  doc.roundedRect(addrX - 4, yPos + 2, 102, 48, 4, 4, 'FD');
  
  let addrY = yPos + 14;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('Verified Address:', addrX, addrY);
  addrY += 9;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  [
    residentialAddress.street || '123 Sample Street',
    residentialAddress.city || 'Sample City',
    residentialAddress.postalCode || '12345',
    `${getCountryFlag(residentialAddress.country || residenceCountry)} ${residentialAddress.country || residenceCountry}`,
  ].forEach(line => {
    doc.text(line, addrX, addrY);
    addrY += 8;
  });
  
  // Verified badge
  doc.setFillColor(...COLORS.success);
  doc.roundedRect(addrX + 50, yPos + 35, 46, 11, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('✓ VERIFIED', addrX + 73, yPos + 42, { align: 'center' });

  // ========== CONTRACTUAL DOCUMENTATION PAGES ==========
  // Each document starts on a new page
  
  // Document 1: Standardised Information Sheet (Fee Information Document)
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, 'STANDARDISED INFORMATION SHEET', yPos);
  yPos = addFeeInformationContent(doc, yPos);
  
  // Document 2: General Terms and Conditions - Accounts
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, 'GENERAL TERMS AND CONDITIONS - ACCOUNTS', yPos);
  yPos = addGTCAccountsContent(doc, yPos);
  
  // Document 3: Standard Fee - Personal/Business Account
  const feeTitle = isCorporate ? 'STANDARD FEE - BUSINESS ACCOUNT' : 'STANDARD FEE - PERSONAL ACCOUNT';
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, feeTitle, yPos);
  yPos = addPersonalAccountFeesContent(doc, yPos, isCorporate);
  
  // Document 4: General Terms and Conditions - Cards
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, 'GENERAL TERMS AND CONDITIONS - CARDS', yPos);
  yPos = addGTCCardsContent(doc, yPos);
  
  // Document 5: Standard Fee - Personal/Business Debit Card
  const cardFeeTitle = isCorporate ? 'STANDARD FEE - BUSINESS DEBIT CARD' : 'STANDARD FEE - PERSONAL DEBIT CARD';
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, cardFeeTitle, yPos);
  yPos = addDebitCardFeesContent(doc, yPos, isCorporate);
  
  // Document 6: Privacy Policy
  doc.addPage();
  yPos = CONTENT_START_Y;
  yPos = drawDocumentHeader(doc, 'PRIVACY POLICY', yPos);
  yPos = addPrivacyPolicyContent(doc, yPos);

  // ========== FINAL PAGE: CONFIRMATIONS & SIGNATURE ==========
  doc.addPage();
  yPos = CONTENT_START_Y;
  
  yPos = drawDocumentHeader(doc, 'CONFIRMATIONS & SIGNATURE', yPos);
  
  // Documents Accepted Box
  const docsBoxStart = yPos;
  doc.setFillColor(...COLORS.boxBg);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 55, 4, 4, 'F');
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 55, 4, 4, 'S');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('Documents Read & Accepted', PAGE_MARGIN + 6, yPos + 10);
  
  const docsAccepted = [
    'Standardised Information Sheet (Fee Information Document)',
    'General Terms and Conditions - Accounts',
    'General Terms and Conditions - Cards',
    isCorporate ? 'Standard Fee - Business Account' : 'Standard Fee - Personal Account',
    isCorporate ? 'Standard Fee - Business Debit Card' : 'Standard Fee - Personal Debit Card',
    'Privacy Policy'
  ];
  
  let docY = yPos + 18;
  docsAccepted.forEach(docName => {
    doc.setFillColor(...COLORS.success);
    doc.circle(PAGE_MARGIN + 10, docY - 1.5, 2.5, 'F');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.text(docName, PAGE_MARGIN + 16, docY);
    docY += 6;
  });
  
  yPos += 62;
  
  // Confirmations Box
  const confBoxStart = yPos;
  doc.setFillColor(...COLORS.boxBg);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 58, 4, 4, 'F');
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(1.5);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 58, 4, 4, 'S');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('Confirmations Accepted', PAGE_MARGIN + 6, yPos + 10);
  
  const confirmations = [
    'I confirm that I have read and accepted all documents above.',
    'I recognise the signed documents as full and sufficient evidence of their content.',
    'I confirm that I have read and understood the PKI Disclosure Statement, Privacy Policy and GTC for the Qualified Electronic Signature Service.',
    'I confirm that the data provided is true and accurate.',
    'I understand my rights under GDPR (access, rectification, erasure, portability).',
    'I acknowledge criminal responsibility for providing false information.'
  ];
  
  let confY = yPos + 18;
  confirmations.forEach((conf) => {
    doc.setFillColor(...COLORS.success);
    doc.circle(PAGE_MARGIN + 10, confY - 1.5, 2.5, 'F');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.gray);
    // Wrap text if needed
    const maxWidth = pageWidth - (PAGE_MARGIN * 2) - 20;
    const lines = doc.splitTextToSize(conf, maxWidth);
    doc.text(lines[0], PAGE_MARGIN + 16, confY);
    confY += 6;
  });
  
  yPos += 66;
  
  // Signature Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('Electronic Signature', PAGE_MARGIN, yPos);
  yPos += 8;
  
  // Signature box with prominent border
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(2);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 50, 4, 4, 'FD');
  
  // Left accent stripe
  doc.setFillColor(...COLORS.accent);
  doc.roundedRect(PAGE_MARGIN, yPos, 5, 50, 4, 0, 'F');
  doc.rect(PAGE_MARGIN + 3, yPos, 2, 50, 'F');
  
  if (data.documentSignature && options?.includeSignature !== false) {
    try {
      // Draw signature larger and centered
      doc.addImage(data.documentSignature, 'PNG', (pageWidth - 100) / 2, yPos + 5, 100, 40);
    } catch {
      doc.setFontSize(11);
      doc.setTextColor(...COLORS.gray);
      doc.text('[Signature captured electronically]', pageWidth / 2, yPos + 25, { align: 'center' });
    }
  } else {
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.gray);
    doc.text('[Electronic Signature on File]', pageWidth / 2, yPos + 25, { align: 'center' });
  }
  
  yPos += 58;
  
  // Signatory info box
  const sigInfoStart = yPos;
  autoTable(doc, {
    startY: yPos + 2,
    head: [],
    body: [
      ['Signatory', fullName],
      ['Email', data.email || 'Not provided'],
      ['Date', data.documentsSignedAt ? new Date(data.documentsSignedAt).toLocaleDateString('en-GB') : currentDate],
      ['Reference', applicationRef],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50, textColor: COLORS.gray },
      1: { textColor: COLORS.navy }
    },
    margin: { left: PAGE_MARGIN + 6, right: PAGE_MARGIN + 6 },
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;
  drawContentBox(doc, sigInfoStart, yPos);
  yPos += 10;
  
  // eIDAS certification
  doc.setFillColor(...COLORS.navy);
  doc.roundedRect(PAGE_MARGIN, yPos, pageWidth - (PAGE_MARGIN * 2), 22, 4, 4, 'F');
  
  // Accent stripe
  doc.setFillColor(...COLORS.accent);
  doc.roundedRect(PAGE_MARGIN, yPos, 6, 22, 3, 0, 'F');
  doc.rect(PAGE_MARGIN + 4, yPos, 2, 22, 'F');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('ELECTRONIC SIGNATURE CERTIFICATION', pageWidth / 2, yPos + 9, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Signed electronically in accordance with EU Regulation 910/2014 (eIDAS)', pageWidth / 2, yPos + 16, { align: 'center' });

  // ========== ADD LETTERHEAD TO ALL PAGES ==========
  const totalPages = doc.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) {
    addLetterhead(doc, i, totalPages);
  }

  return doc;
}

// ============== DOCUMENT CONTENT FUNCTIONS ==============

// Fee Information Document content
function addFeeInformationContent(doc: jsPDF, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Fee Information Document per EU Payment Services Directive', PAGE_MARGIN, yPos);
  doc.text('Doc: PN-FID-V1 | Effective: 05.01.2026', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 10;
  
  // Account Services Table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('Payment Account Services', PAGE_MARGIN, yPos);
  yPos += 6;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Service', 'Fee']],
    body: [
      ['Account Maintenance', 'FREE'],
      ['SEPA Transfers (incoming/outgoing)', 'FREE'],
      ['SWIFT Transfers', '35.00 EUR'],
      ['FASTER Payments', '1.00 EUR'],
      ['Local Payment Fee', '3.50 EUR'],
      ['VISA Direct', '3.50 EUR + 1.50% FX'],
    ],
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;
  
  // ATM Withdrawals Table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text('ATM Withdrawals', PAGE_MARGIN, yPos);
  yPos += 6;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Type', 'Fee']],
    body: [
      ['EEA ATM Withdrawal', '2.00 EUR'],
      ['Non-EEA ATM Withdrawal', '3.50 EUR'],
    ],
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;
  
  return yPos;
}

// GTC Accounts content
function addGTCAccountsContent(doc: jsPDF, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('General Terms and Conditions for Payment Account Services', PAGE_MARGIN, yPos);
  doc.text('Doc: PN-GTC-ACC-V1 | Effective: 08.01.2026', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 12;
  
  const sections = [
    { num: 'I', title: 'General Provisions', desc: 'Scope, definitions, and regulatory framework under PSD2 and Bulgarian law.' },
    { num: 'II', title: 'Account Opening', desc: 'Requirements for account opening, KYC/AML procedures, and client identification.' },
    { num: 'III', title: 'Account Operations', desc: 'Payment services, transfers, and transaction processing rules.' },
    { num: 'IV', title: 'Online Systems', desc: 'Access to online banking, security procedures, and authentication.' },
    { num: 'V', title: 'Fees and Charges', desc: 'Fee structure as per the Standard Fee Schedule.' },
    { num: 'VI', title: 'Client Rights', desc: 'Rights under GDPR including access, rectification, and erasure.' },
    { num: 'VII', title: 'Liability', desc: 'Limitations of liability, force majeure, and dispute resolution.' },
    { num: 'VIII', title: 'Termination', desc: 'Account closure procedures and final settlement.' },
  ];
  
  sections.forEach(section => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.navy);
    doc.text(`${section.num}. ${section.title}`, PAGE_MARGIN, yPos);
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    const lines = doc.splitTextToSize(section.desc, pageWidth - (PAGE_MARGIN * 2));
    doc.text(lines, PAGE_MARGIN, yPos);
    yPos += lines.length * 4 + 6;
  });
  
  return yPos;
}

// Personal Account Fees content
function addPersonalAccountFeesContent(doc: jsPDF, startY: number, isCorporate: boolean = false): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  const tariffLabel = isCorporate ? 'Tariff for Business Account Services' : 'Tariff for Personal Account Services';
  doc.text(tariffLabel, PAGE_MARGIN, yPos);
  doc.text('Doc: PN-FEE-PA-V1 | Effective: 08.01.2026', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 10;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Service', 'Fee']],
    body: [
      ['Account Setup Fee', 'FREE'],
      ['Account Opening Fee', 'FREE'],
      ['Account Closing Fee', 'FREE'],
      ['Monthly Maintenance Fee', 'FREE'],
      ['Annual Fee for holding EUR (if >50,000)', 'Contact Bank'],
      ['Online Payment System Registration', 'FREE'],
      ['SEPA Outgoing (next day)', 'FREE'],
      ['SEPA Outgoing (same day)', 'FREE'],
      ['SWIFT Outgoing', '35.00 EUR'],
      ['FASTER Outgoing', '1.00 EUR'],
      ['Local Payment Fee', '3.50 EUR'],
      ['FX Rate', '1.50%'],
    ],
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;
  
  return yPos;
}

// GTC Cards content
function addGTCCardsContent(doc: jsPDF, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('General Terms and Conditions for Debit and Prepaid Cards', PAGE_MARGIN, yPos);
  doc.text('Doc: PN-GTC-CARD-V1 | Effective: 05.01.2026', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 12;
  
  const sections = [
    { num: 'I', title: 'Subject', desc: 'Framework agreement for card issuance per Payment Services Law.' },
    { num: 'II', title: 'Types of Cards', desc: 'Debit and prepaid Visa/Mastercard cards, virtual and physical.' },
    { num: 'III', title: 'Payment Operations', desc: 'Cash withdrawal, POS payments, online transactions, subscriptions.' },
    { num: 'IV', title: 'Card Issuance', desc: 'Issuance, activation, PIN security, and card validity.' },
    { num: 'V', title: '3D Secure', desc: 'Strong customer authentication for online transactions.' },
    { num: 'VI', title: 'Card Limits', desc: 'Transaction limits, daily limits, and limit modification.' },
    { num: 'VII', title: 'Lost/Stolen Cards', desc: 'Reporting procedures and liability for unauthorized use.' },
    { num: 'VIII', title: 'Fees', desc: 'Card fees as per the Standard Fee - Personal Debit Card.' },
  ];
  
  sections.forEach(section => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.navy);
    doc.text(`${section.num}. ${section.title}`, PAGE_MARGIN, yPos);
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    const lines = doc.splitTextToSize(section.desc, pageWidth - (PAGE_MARGIN * 2));
    doc.text(lines, PAGE_MARGIN, yPos);
    yPos += lines.length * 4 + 6;
  });
  
  return yPos;
}

// Debit Card Fees content
function addDebitCardFeesContent(doc: jsPDF, startY: number, isCorporate: boolean = false): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Tariff for Personal Debit Card Services', PAGE_MARGIN, yPos);
  doc.text('Doc: PN-FEE-DC-V1 | Effective: 05.01.2026', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 10;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Service', 'Fee']],
    body: [
      ['Card Issuing (Physical)', 'FREE'],
      ['Card Issuing (Virtual)', 'FREE'],
      ['Standard European Delivery', '5.99 EUR'],
      ['Express Delivery', '19.99 EUR'],
      ['Card Replacement', '9.99 EUR'],
      ['Change of Operational Limits', 'FREE'],
      ['PIN Reminder', 'FREE'],
      ['Card Blocking/Unblocking', 'FREE'],
      ['ATM Withdrawal (EEA)', '2.00 EUR'],
      ['ATM Withdrawal (Non-EEA)', '3.50 EUR'],
      ['POS Transaction', 'FREE'],
      ['Online Transaction', 'FREE'],
      ['FX Transaction Fee', '1.50%'],
    ],
    theme: 'striped',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;
  
  return yPos;
}

// Privacy Policy content
function addPrivacyPolicyContent(doc: jsPDF, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = startY;
  
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Privacy Policy - Data Protection Information', PAGE_MARGIN, yPos);
  doc.text('Doc: PN-PP-V1 | GDPR Compliant', pageWidth - PAGE_MARGIN, yPos, { align: 'right' });
  yPos += 12;
  
  const sections = [
    { num: '1', title: 'Data Controller', desc: 'PayNovus AD, registered in Bulgaria, is the data controller.' },
    { num: '2', title: 'Data Collected', desc: 'Personal identification data, contact details, financial information, and transaction history.' },
    { num: '3', title: 'Legal Basis', desc: 'Contract performance, legal obligations, legitimate interests, and consent.' },
    { num: '4', title: 'Data Sharing', desc: 'Regulatory authorities, payment networks, and authorized service providers.' },
    { num: '5', title: 'International Transfers', desc: 'Data may be transferred within EEA with appropriate safeguards.' },
    { num: '6', title: 'Data Retention', desc: 'Data retained as required by law and for legitimate business purposes.' },
    { num: '7', title: 'Your Rights', desc: 'Access, rectification, erasure, restriction, portability, and objection.' },
    { num: '8', title: 'AI Processing', desc: 'Automated decision-making for risk assessment and fraud prevention.' },
    { num: '9', title: 'Security', desc: 'Technical and organizational measures to protect personal data.' },
    { num: '10', title: 'Contact', desc: 'Data Protection Officer: dpo@paynovus.com' },
  ];
  
  sections.forEach(section => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.navy);
    doc.text(`${section.num}. ${section.title}`, PAGE_MARGIN, yPos);
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    const lines = doc.splitTextToSize(section.desc, pageWidth - (PAGE_MARGIN * 2));
    doc.text(lines, PAGE_MARGIN, yPos);
    yPos += lines.length * 4 + 6;
  });
  
  return yPos;
}

// Export alias
export { generateComprehensiveApplicationPDF as generateApplicationPDF };
